import { ReactNode, useEffect, useRef, useState } from 'react'
import { IconChevronUp, IconDeviceDesktop, IconDeviceMobile, IconX } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'

const DESKTOP_IFRAME_WIDTH = 1280
const DESKTOP_IFRAME_HEIGHT = 800
const MOBILE_IFRAME_WIDTH = 375
const MOBILE_IFRAME_HEIGHT = 700

interface InspectorPreviewLayoutProps {
  /** URL rendered in the preview iframe. */
  previewUrl: string
  /** Bump this to reload the iframe when the URL itself stays the same. */
  reloadKey?: number
  /** Inspector content — left column on desktop, full width on mobile. */
  children: ReactNode
}

/**
 * Portal editing layout: inspector column plus a live iframe preview of the public page.
 *
 * Desktop (`lg` and up): inspector left (380px), preview right.
 * Mobile: the preview moves behind a fixed bottom button and opens fullscreen, so the
 * inspector gets the whole width.
 *
 * Used by the project design tab and the portfolio detail page.
 */
export default function InspectorPreviewLayout({ previewUrl, reloadKey = 0, children }: InspectorPreviewLayoutProps) {
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop')
  const [iframeLoading, setIframeLoading] = useState(true)
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false)
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // Reset the loading state whenever the iframe reloads
  useEffect(() => {
    setIframeLoading(true)
  }, [previewUrl, reloadKey])

  // Measure container width for desktop scaling
  useEffect(() => {
    const container = previewContainerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width)
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [previewMode, mobilePreviewOpen])

  // Account for browser chrome bar height
  const desktopScale = containerWidth > 0 ? containerWidth / DESKTOP_IFRAME_WIDTH : 0.5

  const previewPanel = (
    <>
      {/* Desktop/Mobile toggle */}
      <div className="flex gap-2 mb-3">
        <Button
          size="sm"
          color={previewMode === 'desktop' ? 'primary' : 'ghost'}
          onClick={() => setPreviewMode('desktop')}
        >
          <IconDeviceDesktop size={18} />
          Desktop
        </Button>
        <Button
          size="sm"
          color={previewMode === 'mobile' ? 'primary' : 'ghost'}
          onClick={() => setPreviewMode('mobile')}
        >
          <IconDeviceMobile size={18} />
          Mobile
        </Button>
      </div>

      {/* Preview container */}
      {previewMode === 'desktop' ? (
        <div ref={previewContainerRef} className="overflow-hidden shadow-lg border border-base-300">
          {/* Browser chrome bar */}
          <div className="bg-secondary flex items-center gap-2 px-3 h-8">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <div className="w-3 h-3 rounded-full bg-[#28c840]" />
            </div>
            {iframeLoading && (
              <div className="loading loading-spinner loading-xs opacity-50 ml-auto" />
            )}
          </div>
          {/* Iframe area */}
          <div
            className="relative bg-secondary overflow-hidden"
            style={{ height: (DESKTOP_IFRAME_HEIGHT * desktopScale) || 400 }}
          >
            <iframe
              key={reloadKey}
              src={previewUrl}
              className="absolute top-0 left-0"
              style={{
                width: DESKTOP_IFRAME_WIDTH,
                height: DESKTOP_IFRAME_HEIGHT,
                transform: `scale(${desktopScale})`,
                transformOrigin: 'top left',
                border: 'none',
              }}
              title="Vorschau"
              onLoad={() => setIframeLoading(false)}
            />
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <div
            className="bg-secondary rounded-[2rem] p-3 shadow-lg relative"
            style={{ width: MOBILE_IFRAME_WIDTH + 24 }}
          >
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="loading loading-spinner loading-md opacity-50" />
              </div>
            )}
            <div
              className="rounded-[1.5rem] overflow-hidden"
              style={{ width: MOBILE_IFRAME_WIDTH, height: MOBILE_IFRAME_HEIGHT }}
            >
              <iframe
                key={reloadKey}
                src={previewUrl}
                style={{
                  width: MOBILE_IFRAME_WIDTH,
                  height: MOBILE_IFRAME_HEIGHT,
                  border: 'none',
                }}
                title="Vorschau"
                onLoad={() => setIframeLoading(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className="flex gap-6">
      {/* Left: Inspector — pb-16 keeps the fixed mobile button off the last section */}
      <div className="w-full lg:w-[380px] lg:shrink-0 flex flex-col gap-5 pb-16 lg:pb-0">
        {children}

        {/* Mobile preview button */}
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
          <button
            className="w-full bg-neutral border-t border-base-300 py-3 flex items-center justify-center gap-2 font-semibold"
            onClick={() => setMobilePreviewOpen(true)}
          >
            <IconChevronUp size={20} />
            Vorschau
          </button>
        </div>
      </div>

      {/* Right: Preview panel (desktop only) */}
      <div className="flex-1 min-w-0 hidden lg:block">
        {previewPanel}
      </div>

      {/* Mobile preview overlay */}
      {mobilePreviewOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex flex-col bg-secondary">
          <div className="flex items-center justify-between px-4 py-3 border-b border-base-300 bg-neutral flex-shrink-0">
            <h3 className="font-semibold">Vorschau</h3>
            <button
              className="btn btn-ghost btn-sm btn-circle"
              onClick={() => setMobilePreviewOpen(false)}
            >
              <IconX size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            {previewPanel}
          </div>
        </div>
      )}
    </div>
  )
}
